import api from './api';

/**
 * Admin client for customer-facing API callers (the /admin/api-customers
 * endpoints). Callers authenticate the /integrations/* APIs with an X-API-Key.
 * The plaintext key is only ever returned by create()/rotate() — store it then.
 */

export interface ApiCustomerAccount {
  id: number;
  ref_field: string; // billto | customercode | brokercode
  ref_recno: number;
  ref_name?: string | null;
}

export interface ApiCustomer {
  id: number;
  customer_code: string;
  name: string;
  enabled: boolean;
  notes?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  key_rotated_by?: string | null;
  key_rotated_at?: string | null;
  accounts: ApiCustomerAccount[];
}

/** Returned once, on create/rotate — the plaintext key. */
export interface ApiKeyReveal {
  id: number;
  customer_code: string;
  api_key: string;
}

export interface LookupItem {
  recno: number;
  name: string;
  city?: string | null;
  state?: string | null;
}

export type RefField = 'billto' | 'customercode' | 'brokercode';

export interface CustomerApiLog {
  id: number;
  received_at?: string | null;
  customer_code?: string | null;
  endpoint: string;
  method: string;
  http_status: number;
  status: string;
  request_ref?: string | null;
  error_message?: string | null;
  duration_ms?: number | null;
}

const base = '/admin/api-customers';

const apiCustomersService = {
  async list(): Promise<ApiCustomer[]> {
    const res = await api.get(base);
    return res.data as ApiCustomer[];
  },

  /** Codes of the integrations deployed in this backend — the only valid
   * customer_code values (a key for any other code can never authenticate). */
  async integrations(): Promise<string[]> {
    const res = await api.get(`${base}/integrations`);
    return res.data as string[];
  },

  async create(data: { customer_code: string; name: string; notes?: string }): Promise<ApiKeyReveal> {
    const res = await api.post(base, data);
    return res.data as ApiKeyReveal;
  },

  async rotate(id: number): Promise<ApiKeyReveal> {
    const res = await api.post(`${base}/${id}/rotate`);
    return res.data as ApiKeyReveal;
  },

  async update(id: number, data: { name?: string; notes?: string; enabled?: boolean }): Promise<ApiCustomer> {
    const res = await api.patch(`${base}/${id}`, data);
    return res.data as ApiCustomer;
  },

  async addAccount(id: number, data: { ref_field: RefField; ref_recno: number }): Promise<ApiCustomerAccount> {
    const res = await api.post(`${base}/${id}/accounts`, data);
    return res.data as ApiCustomerAccount;
  },

  async removeAccount(id: number, accountId: number): Promise<void> {
    await api.delete(`${base}/${id}/accounts/${accountId}`);
  },

  async lookup(field: RefField, q: string): Promise<LookupItem[]> {
    const res = await api.get(`${base}/lookup`, { params: { field, q } });
    return res.data as LookupItem[];
  },

  async activity(params: { customer_code?: string; status?: string; limit?: number } = {}): Promise<CustomerApiLog[]> {
    const res = await api.get(`${base}/activity`, { params });
    return res.data as CustomerApiLog[];
  },
};

export default apiCustomersService;
